import CallError from '../../../utils/CallError';
import { getBadParams, getMissingKeys } from '../../../utils/general';
import { reviewSettings } from '../setup';

/**
 * @typedef ReviewObject
 * @type {Object}
 * @property {string} assetId
 * @property {{version: string}} assetMetadata
 * @property {string} assetType
 * @property {string} authorName
 * @property {string} createdAt
 * @property {string} id
 * @property {bool} isEditable
 * @property {string} lastEditedAt
 * @property {number} rating
 * @property {number} replyCount
 * @property {string} reviewTitle
 * @property {string} reviewText
 *
 * @typedef PageSpec
 * @type {Object}
 * @property {number} count
 * @property {bool} nextPageAvailable
 * @property {number} offset
 * @property {number} totalItems
 */

/**
 * Gets reviews for an application
 * @param {object} params - an object of params
 * @param {string} params.assetId - ID of the plugin you want the reviews for
 * @param {number} [params.limit=5] - Works as page size
 * @param {number} [params.offset=0] - Offset of the results (works as pageIndex)
 * @param {number} [params.accessToken] - IMS access token
 * @param {number} [params.userId] - IMS user ID
 * @param {AbortSignal} [signal] - Abort signal that can be used to abort the fetch request
 * @returns {Promise<{items: ReviewObject[], pageSpec: PageSpec}>}
 * @throws {CallError}
 */
export default async (params = {}, signal = null) => {
  const requiredParams = ['assetId'];
  const paramsRules = {
    assetId: { allowedTypes: ['string'] },
    limit: { allowedTypes: ['number'] },
    offset: { allowedTypes: ['number'] },
    accessToken: { allowedTypes: ['string', 'undefined'] },
    userId: { allowedTypes: ['string', 'undefined'] },
  };
  const missingParams = getMissingKeys(params, requiredParams);
  let badParams = [];
  if (Object.keys(params).length) {
    badParams = getBadParams(params, paramsRules);
  }
  if (missingParams.length || badParams.length) {
    throw new CallError('params', null, 404, { missingParams, badParams });
  }

  const { assetId, accessToken, userId, limit = 5, offset = 0 } = params;
  const isSignedIn = accessToken && userId;
  const endpoint = reviewSettings.endpoint[isSignedIn ? 'get-reviews-auth' : 'get-review'];
  if (!endpoint) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR endpoint' });
  }
  if (!reviewSettings.apiKey) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR api key' });
  }

  const searchParams = new URLSearchParams({
    assetId,
    assetType: 'PLUGIN',
    limit,
    offset,
    orderBy: '-lastUpdated',
    rating: [1, 2, 3, 4, 5],
    filterEmptyTitle: true,
  });
  searchParams.sort();

  const response = await fetch(`${endpoint}?${searchParams.toString()}`, {
    signal,
    method: 'GET',

    headers: {
      authorization: accessToken,
      accept: isSignedIn
        ? 'application/vnd.adobe-review.review-data-detailed-auth-v1+json'
        : 'application/vnd.adobe-review.review-data-detailed-v1+json',
      'x-api-key': reviewSettings.apiKey,
      'x-gw-ims-user-id': userId,
    },
  });
  /**
   * @type {{items: ReviewObject[], pageSpec: PageSpec}}
   */
  let content = await response.text();
  try {
    content = JSON.parse(content);
  } catch (e) {
    // response is not json
  }

  if (!response.ok) {
    throw new CallError('http', content, response.status, { message: response.statusText });
  } else {
    return content;
  }
};
